import { type Configuration, LogLevel, type RedirectRequest } from "@azure/msal-browser";
import type { RuntimeConfig } from "@/config/runtime";

export function buildMsalConfig(config: RuntimeConfig): Configuration {
  return {
    auth: {
      clientId: config.azureClientId,
      authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
      // MSAL v5: must be the redirect bridge page (src/redirect.html), and
      // must be registered as a SPA redirect URI on the Entra app.
      redirectUri: config.azureRedirectUri,
      postLogoutRedirectUri: globalThis.location.origin,
    },
    cache: {
      cacheLocation: "localStorage",
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message) => {
          if (level === LogLevel.Error) console.error("[MSAL]", message);
          else if (level === LogLevel.Warning) console.warn("[MSAL]", message);
        },
      },
    },
  };
}

/**
 * Login scopes. Mirrors frontend/src/config/msal.ts: the Graph delegated
 * scopes are requested up front so users consent at login, which the
 * backend's On-Behalf-Of flow depends on (otherwise OBO fails with
 * interaction_required / invalid_grant).
 */
export function buildLoginRequest(config: RuntimeConfig): RedirectRequest {
  const scopes = ["openid", "profile", "email", "Sites.Read.All", "Files.Read.All"];
  if (config.featureFlags.teamsIntegration) {
    scopes.push("Team.ReadBasic.All", "Channel.ReadBasic.All", "ChannelMessage.Read.All", "Chat.Read");
  }
  if (config.featureFlags.outlookIntegration) {
    scopes.push("Mail.Read", "Calendars.Read");
  }
  return { scopes };
}

/** Scope for calling our backend API. Empty when not configured. */
export function buildApiScopes(config: RuntimeConfig): string[] {
  return config.apiScope ? [config.apiScope] : [];
}
